import { useDispatch, useSelector } from 'react-redux';
import { AuthenticationTokenActions } from 'infrastructure/security/authentication/tokenprovider/authenticationTokenReducer';
import { AuthenticationToken, AuthenticationTokenState } from './authenticationToken.types';

type AuthenticationTokenPersistence = {
  flush: () => Promise<void>;
};

type AuthenticationTokenStateReader = () => Nullable<AuthenticationToken>;

const defaultPersistence: AuthenticationTokenPersistence = {
  flush: async () => {},
};

const defaultStateReader: AuthenticationTokenStateReader = () => null;

let authenticationTokenPersistence: AuthenticationTokenPersistence = defaultPersistence;
let authenticationTokenStateReader: AuthenticationTokenStateReader = defaultStateReader;

export const configureAuthenticationTokenPersistence = (persistence: AuthenticationTokenPersistence) => {
  authenticationTokenPersistence = persistence;
};

export const configureAuthenticationTokenStateReader = (reader: AuthenticationTokenStateReader) => {
  authenticationTokenStateReader = reader;
};

export const getStoredAuthenticationToken = (): Nullable<AuthenticationToken> => authenticationTokenStateReader();

const saveToken = (authenticationToken: AuthenticationToken) => ({
  type: AuthenticationTokenActions.SAVE_TOKEN,
  authenticationToken,
});

const clearToken = () => ({
  type: AuthenticationTokenActions.CLEAR_TOKEN,
});

interface AuthenticationTokenRepository {
  getToken: () => Promise<AuthenticationToken>;
  saveToken: (authToken: AuthenticationToken) => Promise<void>;
  clearToken: () => Promise<void>;
}

const useAuthenticationTokenRepository = (): AuthenticationTokenRepository => {
  const dispatch = useDispatch();
  const authenticationToken: AuthenticationToken = useSelector(
    (state: { authToken: AuthenticationTokenState }) => state?.authToken?.authenticationToken as AuthenticationToken,
  );

  const getAccessToken = (): AuthenticationToken => getStoredAuthenticationToken() || authenticationToken;

  const saveAccessToken = async (authToken: AuthenticationToken): Promise<void> => {
    dispatch(saveToken(authToken));
    await authenticationTokenPersistence.flush();
  };

  const clearAccessToken = async (): Promise<void> => {
    dispatch(clearToken());
    await authenticationTokenPersistence.flush();
  };
  return {
    getToken: () => Promise.resolve(getAccessToken()),
    saveToken: saveAccessToken,
    clearToken: clearAccessToken,
  };
};

export default useAuthenticationTokenRepository;
